import { vehicleOwnerTemplateDictionaryItems } from "./vehicleOwnerTemplateDictionaryItems";
import { startCase } from "lodash";

export type TemplateDictionaryParent =
  | "adjuster"
  | "incidentReportSource"
  | "propertyDamageAdjuster"
  | "medPayAdjuster"
  | "client"
  | "contact"
  | "date"
  | "defendant"
  | "firm"
  | "firmOffice"
  | "firmUser"
  | "clientInsurance"
  | "defendantInsurance"
  | "matter"
  | "custom"
  | "primaryContact"
  | "provider"
  | "signer"
  | "userSigner"
  | "unknown"
  | "clientVehicle"
  | "defendantVehicle"
  | "witness";
export type TemplateDictionaryType = "string" | "number" | "date";

export interface TemplateDictionaryItem {
  title: string;
  variable: string;
  type: TemplateDictionaryType;
  parent: TemplateDictionaryParent;
  lockedAtGeneration?: boolean;
  lockedDisplayAsMultiline?: boolean;
  allowInstanceNickname?: boolean;
  options?: string[];
  insuranceContactOptions?: string[];
  baseVariable?: string;
  exampleValue?: string;
  isSignatureVariable?: boolean;
  hideFormatting?: boolean;
  requiresCustomInput?: boolean;
  customInputLabel?: string;
  customInputPlaceholder?: string;
  customInputFallback?: string;
}

export function formatTemplateDictionaryTitle(title: string): string {
  return title.replace(/\b[A-Z][a-z]+\b/g, (word, index) =>
    index === 0 ? word : word.toLowerCase(),
  );
}

const templateDictionaryItemsBase: TemplateDictionaryItem[] = [
  // #region Firm
  {
    title: "Firm Name",
    variable: "firmName",
    type: "string",
    parent: "firm",
    exampleValue: "Smith & Associates",
  },
  {
    title: "Firm Office Address Line 1",
    variable: "firmOfficeAddressLine1",
    type: "string",
    parent: "firmOffice",
    exampleValue: "123 Main St, Suite 100",
  },
  {
    title: "Firm Office Address Line 2",
    variable: "firmOfficeAddressLine2",
    type: "string",
    parent: "firmOffice",
    exampleValue: "New York, NY 10001",
  },
  {
    title: "Firm Office Address",
    variable: "firmOfficeAddress",
    type: "string",
    parent: "firmOffice",
    exampleValue: "123 Main St, Suite 100, New York, NY 10001",
  },
  //#endregion
  // #region Firm User
  {
    title: "Firm Office Phone Number",
    variable: "firmOfficePhoneNumber",
    type: "string",
    parent: "firmOffice",
    exampleValue: "(555) 123-4567",
    hideFormatting: true,
  },
  {
    title: "User First Name",
    variable: "userFirstName",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "Alex",
  },
  {
    title: "User Middle Name",
    variable: "userMiddleName",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "John",
  },
  {
    title: "User Last Name",
    variable: "userLastName",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "Rivera",
  },
  {
    title: "User Name",
    variable: "userName",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "Alex Rivera",
  },
  {
    title: "User Email",
    variable: "userEmail",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "alex@example.com",
    hideFormatting: true,
  },
  {
    title: "User Phone",
    variable: "userPhone",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "(555) 123-4567",
    hideFormatting: true,
  },
  {
    title: "User Bar Number",
    variable: "userBarNumber",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "123456",
  },
  {
    title: "User Initials",
    variable: "userInitials",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "AR",
  },
  {
    title: "User Middle Initial",
    variable: "userMiddleInitial",
    type: "string",
    parent: "firmUser",
    allowInstanceNickname: true,
    exampleValue: "J",
  },
  //#endregion
  // #region Contact
  {
    title: "Contact Name",
    variable: "contactName",
    type: "string",
    parent: "contact",
    exampleValue: "John Smith",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact First Name",
    variable: "contactFirstName",
    type: "string",
    parent: "contact",
    exampleValue: "John",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Middle Name",
    variable: "contactMiddleName",
    type: "string",
    parent: "contact",
    exampleValue: "A.",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Last Name",
    variable: "contactLastName",
    type: "string",
    parent: "contact",
    exampleValue: "Smith",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Address Line 1",
    variable: "contactAddressLine1",
    type: "string",
    parent: "contact",
    exampleValue: "456 Oak Ave, Suite 200",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Address Line 2",
    variable: "contactAddressLine2",
    type: "string",
    parent: "contact",
    exampleValue: "Los Angeles, CA 90001",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Address",
    variable: "contactAddress",
    type: "string",
    parent: "contact",
    exampleValue: "456 Oak Ave, Los Angeles, CA 90001",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Street 1",
    variable: "contactAddressStreet1",
    type: "string",
    parent: "contact",
    exampleValue: "456 Oak Ave",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Street 2",
    variable: "contactAddressStreet2",
    type: "string",
    parent: "contact",
    exampleValue: "Suite 200",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact City",
    variable: "contactAddressCity",
    type: "string",
    parent: "contact",
    exampleValue: "Los Angeles",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact State",
    variable: "contactAddressState",
    type: "string",
    parent: "contact",
    exampleValue: "CA",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact ZIP Code",
    variable: "contactAddressZipCode",
    type: "string",
    parent: "contact",
    exampleValue: "90001",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Country",
    variable: "contactAddressCountry",
    type: "string",
    parent: "contact",
    exampleValue: "USA",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Title",
    variable: "contactTitle",
    type: "string",
    parent: "contact",
    exampleValue: "Mr.",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Email",
    variable: "contactEmail",
    type: "string",
    parent: "contact",
    exampleValue: "john@example.com",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
    hideFormatting: true,
  },
  {
    title: "Contact Phone",
    variable: "contactPhone",
    type: "string",
    parent: "contact",
    exampleValue: "(555) 123-4567",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
    hideFormatting: true,
  },
  {
    title: "Contact Pronoun (he/she/they)",
    variable: "contactPronoun",
    type: "string",
    parent: "contact",
    exampleValue: "they",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Date of Birth",
    variable: "contactDateOfBirth",
    type: "date",
    parent: "contact",
    exampleValue: "January 1, 1990",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Objective Pronoun (him/her/them)",
    variable: "contactObjectivePronoun",
    type: "string",
    parent: "contact",
    exampleValue: "them",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Possessive Pronoun (his/her/their)",
    variable: "contactPossessivePronoun",
    type: "string",
    parent: "contact",
    exampleValue: "their",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
  },
  {
    title: "Contact Fax",
    variable: "contactFax",
    type: "string",
    parent: "contact",
    exampleValue: "(555) 987-6543",
    options: ["client", "defendant", "primaryContact", "provider", "witness"],
    hideFormatting: true,
  },
  {
    title: "Contact SSN",
    variable: "contactSSN",
    type: "string",
    parent: "contact",
    exampleValue: "123-45-6789",
    options: ["client", "defendant"],
    hideFormatting: true,
  },
  // #endregion
  // #region MedPay Adjuster
  {
    title: "MedPay Adjuster First",
    variable: "medPayAdjusterFirstName",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "Jane",
  },
  {
    title: "MedPay Adjuster Last",
    variable: "medPayAdjusterLastName",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "Smith",
  },
  {
    title: "MedPay Adjuster Title",
    variable: "medPayAdjusterTitle",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "Ms.",
  },
  {
    title: "MedPay Adjuster Address P1",
    variable: "medPayAdjusterAddressLine1",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "123 Main St, Suite 200",
  },
  {
    title: "MedPay Adjuster Address P2",
    variable: "medPayAdjusterAddressLine2",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "Los Angeles, CA 90001",
  },
  {
    title: "MedPay Adjuster Email",
    variable: "medPayAdjusterEmail",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "jane.smith@example.com",
    hideFormatting: true,
  },
  {
    title: "MedPay Adjuster Fax",
    variable: "medPayAdjusterFax",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "(555) 987-6543",
    hideFormatting: true,
  },
  {
    title: "MedPay Adjuster Phone",
    variable: "medPayAdjusterPhone",
    type: "string",
    parent: "medPayAdjuster",
    exampleValue: "(555) 123-4567",
    hideFormatting: true,
  },
  // #endregion
  // #region Incident Report Source
  {
    title: "Incident Report Source",
    variable: "incidentReportSourceName",
    type: "string",
    parent: "incidentReportSource",
    exampleValue: "Los Angeles Police Department",
  },
  {
    title: "Incident Report Source Address P1",
    variable: "incidentReportSourceAddressLine1",
    type: "string",
    parent: "incidentReportSource",
    exampleValue: "123 Main St",
  },
  {
    title: "Incident Report Source Address P2",
    variable: "incidentReportSourceAddressLine2",
    type: "string",
    parent: "incidentReportSource",
    exampleValue: "Los Angeles, CA 90001",
  },
  {
    title: "Incident Report Source Phone",
    variable: "incidentReportSourcePhone",
    type: "string",
    parent: "incidentReportSource",
    exampleValue: "(555) 123-4567",
    hideFormatting: true,
  },
  // #endregion
  // #region Property Damage Adjuster
  {
    title: "PD Adjuster First",
    variable: "pdAdjusterFirstName",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "Jane",
  },
  {
    title: "PD Adjuster Last",
    variable: "pdAdjusterLastName",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "Smith",
  },
  {
    title: "PD Adjuster Address P1",
    variable: "pdAdjusterAddressLine1",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "123 Main St, Suite 200",
  },
  {
    title: "PD Adjuster Address P2",
    variable: "pdAdjusterAddressLine2",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "Los Angeles, CA 90001",
  },
  {
    title: "PD Adjuster Email",
    variable: "pdAdjusterEmail",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "jane.smith@example.com",
    hideFormatting: true,
  },
  {
    title: "PD Adjuster Fax",
    variable: "pdAdjusterFax",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "(555) 987-6543",
    hideFormatting: true,
  },
  {
    title: "PD Adjuster Phone",
    variable: "pdAdjusterPhone",
    type: "string",
    parent: "propertyDamageAdjuster",
    exampleValue: "(555) 123-4567",
    hideFormatting: true,
  },
  // #endregion
  // #region Defendant
  {
    title: "All Defendants",
    variable: "allDefendants",
    type: "string",
    parent: "defendant",
    exampleValue: "Walmart, Dave Jones",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  // #endregion
  // #region Matter
  {
    title: "All Health Providers",
    variable: "allHealthProviders",
    type: "string",
    parent: "matter",
    exampleValue: "Hope Hospital, Champion Chiropractic, CARE Ambulance",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "All Providers Dates",
    variable: "allProvidersDates",
    type: "string",
    parent: "matter",
    exampleValue: "Champion Chiropractic 01/01/2018 - 02/15/2018",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "All Providers Final",
    variable: "allProvidersFinal",
    type: "string",
    parent: "matter",
    exampleValue: "Hope Hospital $100.00",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "All Providers Original",
    variable: "allProvidersOriginal",
    type: "string",
    parent: "matter",
    exampleValue: "Hope Hospital $1,000.00",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "All Providers Original Final",
    variable: "allProvidersOriginalFinal",
    type: "string",
    parent: "matter",
    exampleValue: "Hope Hospital - Original: $2,500.00 Final: $1,000.00",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "All Providers Original Final Adjusted Insurance Paid",
    variable: "allProvidersOrigFinalAdjustedInsPaid",
    type: "string",
    parent: "matter",
    exampleValue:
      "Hope Hospital - Original: $5,000.00 Final: $3,500.00 Adjusted: $1,300.00 Insurance: $200.00",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "All Health Insurances Original Final Adjusted",
    variable: "allHealthInsOriginalFinalAdjusted",
    type: "string",
    parent: "matter",
    exampleValue:
      "Kaiser - Original: $5,000.00 Final: $3,500.00 Adjusted: $1,300.00",
    lockedAtGeneration: true,
    lockedDisplayAsMultiline: true,
  },
  {
    title: "Total Costs",
    variable: "totalCosts",
    type: "string",
    parent: "matter",
    exampleValue: "$5,000.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total Fees",
    variable: "totalFees",
    type: "string",
    parent: "matter",
    exampleValue: "$8,333.33",
    lockedAtGeneration: true,
  },
  {
    title: "Total Health Insurance Original",
    variable: "totalHealthInsuranceOriginal",
    type: "string",
    parent: "matter",
    exampleValue: "$12,500.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total MedPay",
    variable: "totalMedpay",
    type: "string",
    parent: "matter",
    exampleValue: "$2,500.00",
    lockedAtGeneration: true,
  },
  {
    title: "Net Client Issued",
    variable: "netClientIssued",
    type: "string",
    parent: "matter",
    exampleValue: "$9,166.67",
    lockedAtGeneration: true,
  },
  {
    title: "Total Final Bills",
    variable: "totalFinalBills",
    type: "string",
    parent: "matter",
    exampleValue: "$3,500.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total Original Bills",
    variable: "totalOriginalBills",
    type: "string",
    parent: "matter",
    exampleValue: "$5,000.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total Settlement",
    variable: "totalSettlement",
    type: "string",
    parent: "matter",
    exampleValue: "$25,000.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total Still Owed",
    variable: "totalStillOwed",
    type: "string",
    parent: "matter",
    exampleValue: "$1,300.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total Third Party",
    variable: "totalThirdParty",
    type: "string",
    parent: "matter",
    exampleValue: "$20,000.00",
    lockedAtGeneration: true,
  },
  {
    title: "Total UM UIM",
    variable: "totalUmUim",
    type: "string",
    parent: "matter",
    exampleValue: "$5,000.00",
    lockedAtGeneration: true,
  },
  // #endregion
  // #region Insurance
  {
    title: "Client Insurance Policy Number",
    variable: "clientInsurancePolicyNumber",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "ABC1234567",
  },
  {
    title: "Client Insurance Claim Number",
    variable: "clientInsuranceClaimNumber",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "CLM-987654",
  },
  {
    title: "Client Insurance Group Number",
    variable: "clientInsuranceGroupNumber",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "GRP-987654",
  },
  {
    title: "Client Insurance Member Number",
    variable: "clientInsuranceMemberNumber",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "MEM-987654",
  },
  {
    title: "Client Insurance Policy Limits",
    variable: "clientInsurancePolicyLimits",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "$50,000 / $100,000",
  },
  {
    title: "Client Insurance Insured First Name",
    variable: "clientInsuranceInsuredFirstName",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "John",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Last Name",
    variable: "clientInsuranceInsuredLastName",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "Smith",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Name",
    variable: "clientInsuranceInsuredName",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "John Smith",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Fax",
    variable: "clientInsuranceInsuredFax",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "(555) 987-6543",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Phone",
    variable: "clientInsuranceInsuredPhone",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "(555) 123-4567",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Email",
    variable: "clientInsuranceInsuredEmail",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "john@example.com",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Address Line 1",
    variable: "clientInsuranceInsuredAddressLine1",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "123 Main St",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Client Insurance Insured Address Line 2",
    variable: "clientInsuranceInsuredAddressLine2",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "Springfield, IL 62704",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Policy Number",
    variable: "defendantInsurancePolicyNumber",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "DEF7654321",
  },
  {
    title: "Defendant Insurance Claim Number",
    variable: "defendantInsuranceClaimNumber",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "CLM-123456",
  },
  {
    title: "Defendant Insurance Group Number",
    variable: "defendantInsuranceGroupNumber",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "GRP-123456",
  },
  {
    title: "Defendant Insurance Member Number",
    variable: "defendantInsuranceMemberNumber",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "MEM-123456",
  },
  {
    title: "Defendant Insurance Policy Limits",
    variable: "defendantInsurancePolicyLimits",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "$50,000 / $100,000",
  },
  {
    title: "Defendant Insurance Insured First Name",
    variable: "defendantInsuranceInsuredFirstName",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "John",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Last Name",
    variable: "defendantInsuranceInsuredLastName",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "Smith",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Name",
    variable: "defendantInsuranceInsuredName",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "John Smith",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Fax",
    variable: "defendantInsuranceInsuredFax",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "(555) 987-6543",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Phone",
    variable: "defendantInsuranceInsuredPhone",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "(555) 123-4567",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Email",
    variable: "defendantInsuranceInsuredEmail",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "john@example.com",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Address Line 1",
    variable: "defendantInsuranceInsuredAddressLine1",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "123 Main St",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  {
    title: "Defendant Insurance Insured Address Line 2",
    variable: "defendantInsuranceInsuredAddressLine2",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "Springfield, IL 62704",
    insuranceContactOptions: [
      "BodilyInjuryAdjuster",
      "PropertyDamageAdjuster",
      "MedPayAdjuster",
      "AdjusterSupervisor",
      "Insurer",
      "PolicyHolder",
      "SubrogationVendor",
    ],
  },
  // #endregion
  // #region Matter
  {
    title: "Contact Driver License Number",
    variable: "contactDriverLicenseNumber",
    type: "string",
    parent: "contact",
    exampleValue: "D1234567",
    options: ["client", "defendant"],
  },
  {
    title: "Contact Driver License State",
    variable: "contactDriverLicenseState",
    type: "string",
    parent: "contact",
    exampleValue: "CA",
    options: ["client", "defendant"],
  },
  {
    title: "Matter Title",
    variable: "matterTitle",
    type: "string",
    parent: "matter",
    exampleValue: "Personal Injury Matter",
  },
  {
    title: "Incident Type",
    variable: "incidentType",
    type: "string",
    parent: "matter",
    exampleValue: "Personal Injury",
  },
  {
    title: "Matter Status",
    variable: "matterStatus",
    type: "string",
    parent: "matter",
    exampleValue: "Active",
  },
  {
    title: "Client Injury Description",
    variable: "matterClientInjuryDescription",
    type: "string",
    parent: "matter",
    exampleValue: "Client suffered a broken arm in a car accident.",
  },
  {
    title: "Incident Street 1",
    variable: "incidentAddressStreet1",
    type: "string",
    parent: "matter",
    exampleValue: "123 Main St",
  },
  {
    title: "Incident Street 2",
    variable: "incidentAddressStreet2",
    type: "string",
    parent: "matter",
    exampleValue: "Suite 200",
  },
  {
    title: "Incident City",
    variable: "incidentAddressCity",
    type: "string",
    parent: "matter",
    exampleValue: "Springfield",
  },
  {
    title: "Incident State",
    variable: "incidentAddressState",
    type: "string",
    parent: "matter",
    exampleValue: "IL",
  },
  {
    title: "Incident ZIP Code",
    variable: "incidentAddressZipCode",
    type: "string",
    parent: "matter",
    exampleValue: "62704",
  },
  {
    title: "Incident Country",
    variable: "incidentAddressCountry",
    type: "string",
    parent: "matter",
    exampleValue: "USA",
  },
  {
    title: "Incident Address Line 1",
    variable: "incidentAddressLine1",
    type: "string",
    parent: "matter",
    exampleValue: "123 Main St, Suite 200",
  },
  {
    title: "Incident Address Line 2",
    variable: "incidentAddressLine2",
    type: "string",
    parent: "matter",
    exampleValue: "Springfield, IL 62704",
  },

  {
    title: "Incident Location",
    variable: "incidentLocation",
    type: "string",
    parent: "matter",
    exampleValue: "123 Main St, Springfield, IL 62704",
  },
  {
    title: "Incident Location Details",
    variable: "incidentAddressDetails",
    type: "string",
    parent: "matter",
    exampleValue: "Near the north entrance of the parking lot",
  },
  {
    title: "Incident County",
    variable: "incidentCounty",
    type: "string",
    parent: "matter",
    exampleValue: "Cook County",
  },
  {
    title: "Incident Time",
    variable: "incidentTime",
    type: "string",
    parent: "matter",
    exampleValue: "2:30 PM",
  },
  {
    title: "Date Retained",
    variable: "matterDateRetained",
    type: "date",
    parent: "matter",
    exampleValue: "March 20, 2024",
  },
  {
    title: "Date Filed",
    variable: "matterFiledDate",
    type: "date",
    parent: "matter",
    exampleValue: "April 5, 2024",
  },
  // #endregion
  ...vehicleOwnerTemplateDictionaryItems,
  // #region Client Vehicle
  {
    title: "Client Vehicle Year",
    variable: "clientVehicleYear",
    type: "number",
    parent: "clientVehicle",
    exampleValue: "2020",
  },
  {
    title: "Client Vehicle Make",
    variable: "clientVehicleMake",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "Toyota",
  },
  {
    title: "Client Vehicle Model",
    variable: "clientVehicleModel",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "Camry",
  },
  {
    title: "Client Vehicle Color",
    variable: "clientVehicleColor",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "Red",
  },
  {
    title: "Client Vehicle License Plate Number",
    variable: "clientVehicleLicensePlateNumber",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "ABC123",
  },
  {
    title: "Client Vehicle Registration State",
    variable: "clientVehicleRegistrationState",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "California",
  },
  {
    title: "Client Vehicle VIN",
    variable: "clientVehicleVin",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "12345678901234567",
  },
  {
    title: "Client Vehicle",
    variable: "clientVehicle",
    type: "string",
    parent: "clientVehicle",
    exampleValue: "2020 Toyota Camry",
  },
  // #endregion
  // #region Defendant Vehicle
  {
    title: "Defendant Vehicle Year",
    variable: "defendantVehicleYear",
    type: "number",
    parent: "defendantVehicle",
    exampleValue: "2020",
  },
  {
    title: "Defendant Vehicle Make",
    variable: "defendantVehicleMake",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "Toyota",
  },
  {
    title: "Defendant Vehicle Model",
    variable: "defendantVehicleModel",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "Camry",
  },
  {
    title: "Defendant Vehicle Color",
    variable: "defendantVehicleColor",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "Red",
  },
  {
    title: "Defendant Vehicle License Plate Number",
    variable: "defendantVehicleLicensePlateNumber",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "ABC123",
  },
  {
    title: "Defendant Vehicle Registration State",
    variable: "defendantVehicleRegistrationState",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "California",
  },
  {
    title: "Defendant Vehicle VIN",
    variable: "defendantVehicleVin",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "12345678901234567",
  },
  {
    title: "Defendant Vehicle",
    variable: "defendantVehicle",
    type: "string",
    parent: "defendantVehicle",
    exampleValue: "2020 Toyota Camry",
  },
  // #endregion
  // #region Insurance Subrogation
  {
    title: "Client Insurance Subrogation File Number",
    variable: "clientInsuranceSubrogationFileNumber",
    type: "string",
    parent: "clientInsurance",
    exampleValue: "SUB-123456",
  },
  {
    title: "Defendant Insurance Subrogation File Number",
    variable: "defendantInsuranceSubrogationFileNumber",
    type: "string",
    parent: "defendantInsurance",
    exampleValue: "SUB-654321",
  },
  // #endregion
  // #region Date
  {
    title: "Today's Date",
    variable: "today",
    type: "date",
    parent: "date",
    exampleValue: "March 15, 2024",
  },
  {
    title: "Date of Loss",
    variable: "dateOfLoss",
    type: "date",
    parent: "date",
    exampleValue: "January 1, 2024",
  },
  // #endregion
  // #region Signer
  {
    title: "Signer Full Name",
    variable: "Signer_text_fullName",
    type: "string",
    parent: "signer",
    exampleValue: "John Smith",
    hideFormatting: true,
  },
  {
    title: "Signer Initials",
    variable: "Signer_text_initials",
    type: "string",
    parent: "signer",
    exampleValue: "JS",
    hideFormatting: true,
  },
  {
    title: "Signer Signature",
    variable: "Signer_signature",
    type: "string",
    parent: "signer",
    exampleValue: "John Smith",
    isSignatureVariable: true,
    hideFormatting: true,
  },
  {
    title: "Signer Date",
    variable: "Signer_date_Date",
    type: "date",
    parent: "signer",
    exampleValue: "March 15, 2024",
  },
  {
    title: "Signer Custom Text Field",
    variable: "Signer_text_fieldName",
    type: "string",
    parent: "signer",
    exampleValue: "Potato",
    hideFormatting: true,
    requiresCustomInput: true,
    customInputLabel: "Field Name",
    customInputPlaceholder: "Custom field name",
    customInputFallback: "fieldName",
  },
  {
    title: "Signer Custom Date Field",
    variable: "Signer_date_fieldName",
    type: "date",
    parent: "signer",
    exampleValue: "March 15, 2024",
    requiresCustomInput: true,
    customInputLabel: "Field Name",
    customInputPlaceholder: "Custom date name",
    customInputFallback: "fieldName",
  },
  // #endregion
  // #region User Signer
  {
    title: "User Signer Full Name",
    variable: "UserSigner_text_fullName",
    type: "string",
    parent: "userSigner",
    exampleValue: "Alex Rivera",
    hideFormatting: true,
  },
  {
    title: "User Signer Initials",
    variable: "UserSigner_text_initials",
    type: "string",
    parent: "userSigner",
    exampleValue: "AR",
    hideFormatting: true,
  },
  {
    title: "User Signer Signature",
    variable: "UserSigner_signature",
    type: "string",
    parent: "userSigner",
    exampleValue: "Alex Rivera",
    isSignatureVariable: true,
    hideFormatting: true,
  },
  {
    title: "User Signer Date",
    variable: "UserSigner_date_Date",
    type: "date",
    parent: "userSigner",
    exampleValue: "March 15, 2024",
  },
  {
    title: "User Signer Custom Text Field",
    variable: "UserSigner_text_fieldName",
    type: "string",
    parent: "userSigner",
    exampleValue: "Custom Text",
    hideFormatting: true,
    requiresCustomInput: true,
    customInputLabel: "Field Name",
    customInputPlaceholder: "Custom field name",
    customInputFallback: "fieldName",
  },
  {
    title: "User Signer Custom Date Field",
    variable: "UserSigner_date_fieldName",
    type: "date",
    parent: "userSigner",
    exampleValue: "March 15, 2024",
    requiresCustomInput: true,
    customInputLabel: "Field Name",
    customInputPlaceholder: "Custom date name",
    customInputFallback: "fieldName",
  },
  // #endregion
] as const;

export const templateDictionaryItems: TemplateDictionaryItem[] =
  templateDictionaryItemsBase
    .flatMap((item) => {
      if (!item.options?.length && !item.insuranceContactOptions?.length) {
        return [item];
      }
      let optionItems: TemplateDictionaryItem[] = [];
      if (item.options?.length) {
        optionItems = item.options.map((option) => ({
          ...item,
          title: item.title.replace("Contact", startCase(option)),
          variable: item.variable.replace("contact", option),
          parent: option as TemplateDictionaryParent,
          options: undefined,
        }));
      }

      if (item.insuranceContactOptions?.length) {
        optionItems = item.insuranceContactOptions.map((option) => ({
          ...item,
          title: item.title.replace("Insured", startCase(option)),
          variable: item.variable.replace("Insured", option),
          parent: item.parent,
          options: undefined,
        }));
      }

      return [item, ...optionItems];
    })
    .map((item) => ({
      ...item,
      title: formatTemplateDictionaryTitle(item.title),
    }));
